// ============================================
// Manufacturing Admin — shared constants & helpers
// ============================================
import { t, dateLocale } from "i18n";
import { apiErrorMessage } from "utils/apiErrors";

// A wearable is `in_validation` until it completes a checklist-driven flow,
// then becomes `validated` (earned, never set directly). After that, only the
// post-validation lifecycle statuses are settable via PATCH.
//
//   in_validation ─(all steps pass)─▶ validated ─▶ deployed ⇄ returned
//                                          │            │          │
//                                          └────────────┴──────────┴─▶ retired
//                          (any status) ───────────────────────────────▶ scrapped
export const TERMINAL_STATUSES = ["retired", "scrapped"];

// Which statuses each state can PATCH to. `validated` is intentionally absent:
// it is earned by completing the checklist, not settable. Scrap is handled
// separately (allowed from any non-terminal state, needs a reason).
export const LIFECYCLE_ACTIONS = {
  validated: ["deployed", "retired"],
  deployed: ["returned", "retired"],
  returned: ["deployed", "retired"],
};

/**
 * Settable statuses for the current state, so the UI only offers legal moves.
 * `scrapped` is appended for any non-terminal state (ANY → scrapped).
 * `in_validation` returns just `scrapped` — the rest is earned via steps.
 */
export const getLifecycleActions = (status) => {
  const next = [...(LIFECYCLE_ACTIONS[status] || [])];
  if (!TERMINAL_STATUSES.includes(status)) next.push("scrapped");
  return next;
};

// The verb shown on a lifecycle button, distinct from the resulting status
// label (e.g. the button reads "Deploy" and moves the device to "Deployed").
// Values are i18n keys under admin.manufacturing.lifecycleActions; translate
// at display time via getActionLabel().
export const ACTION_LABEL_KEYS = {
  deployed: "admin.manufacturing.lifecycleActions.deployed",
  returned: "admin.manufacturing.lifecycleActions.returned",
  retired: "admin.manufacturing.lifecycleActions.retired",
  scrapped: "admin.manufacturing.lifecycleActions.scrapped",
};

export const getActionLabel = (status) =>
  ACTION_LABEL_KEYS[status] ? t(ACTION_LABEL_KEYS[status]) : null;

// Display metadata per status: { labelKey (i18n), color (text), bg }
export const STATUS_META = {
  in_validation: {
    labelKey: "admin.manufacturing.statuses.in_validation",
    color: "#6366F1",
    bg: "#EEF2FF",
  },
  validated: {
    labelKey: "admin.manufacturing.statuses.validated",
    color: "#2563EB",
    bg: "#EFF6FF",
  },
  deployed: {
    labelKey: "admin.manufacturing.statuses.deployed",
    color: "#16A34A",
    bg: "#DCFCE7",
  },
  returned: {
    labelKey: "admin.manufacturing.statuses.returned",
    color: "#D97706",
    bg: "#FFFBEB",
  },
  retired: {
    labelKey: "admin.manufacturing.statuses.retired",
    color: "#6B7280",
    bg: "#F3F4F6",
  },
  scrapped: {
    labelKey: "admin.manufacturing.statuses.scrapped",
    color: "#DC2626",
    bg: "#FEF2F2",
  },
};

// Returns { label, color, bg } with the label translated at call time.
export const getStatusMeta = (status) => {
  const meta = STATUS_META[status];
  if (meta) {
    return { label: t(meta.labelKey), color: meta.color, bg: meta.bg };
  }
  return {
    label: status || t("common.status.unknown"),
    color: "#6B7280",
    bg: "#F3F4F6",
  };
};

// Ordered list for filter dropdowns (labels translated at call time)
export const getStatusOptions = () =>
  Object.keys(STATUS_META).map((id) => ({
    id,
    label: t(STATUS_META[id].labelKey),
  }));

// ── Validation flow ─────────────────────────────────────────────────
// The device moves through these ordered sub-stages while in_validation; the
// server labels the tree, so this metadata is only for filters/board headers.
// `complete` is the terminal validation_stage once validated.
export const VALIDATION_STAGES = [
  "baseline",
  "overmould",
  "bias_removal",
  "centrifuge",
  "drop",
];

export const VALIDATION_STAGE_META = {
  baseline: { labelKey: "admin.manufacturing.stages.baseline" },
  overmould: { labelKey: "admin.manufacturing.stages.overmould" },
  bias_removal: { labelKey: "admin.manufacturing.stages.bias_removal" },
  centrifuge: { labelKey: "admin.manufacturing.stages.centrifuge" },
  drop: { labelKey: "admin.manufacturing.stages.drop" },
  complete: { labelKey: "admin.manufacturing.stages.complete" },
};

// Returns { label } with the label translated at call time.
export const getStageMeta = (stage) => {
  const meta = VALIDATION_STAGE_META[stage];
  return meta ? { label: t(meta.labelKey) } : { label: stage || "—" };
};

export const getStageOptions = () =>
  VALIDATION_STAGES.map((id) => ({
    id,
    label: t(VALIDATION_STAGE_META[id].labelKey),
  }));

// Suggested structured data to capture per step. `data` is free-form JSON the
// API stores as-is; these are the keys the downstream consumers expect. Steps
// not listed here just take a free-text note. Coordinate exact keys with
// whoever reads them before changing.
// `labelKey` / `placeholderKey` are i18n keys (translate at render time);
// a literal `placeholder` is an example value that is not translated.
const STEP_FIELD_KEYS = "admin.manufacturing.stepFields";
export const STEP_DATA_FIELDS = {
  factory_test: [
    {
      key: "mac",
      labelKey: `${STEP_FIELD_KEYS}.mac`,
      placeholder: "A1B2C3D4E5F6",
    },
  ],
  firmware_upload: [
    {
      key: "firmware_version",
      labelKey: `${STEP_FIELD_KEYS}.firmwareVersion`,
      placeholder: "1.2.3",
    },
    {
      key: "calibration_id",
      labelKey: `${STEP_FIELD_KEYS}.calibrationId`,
      placeholder: "cal-0001",
    },
  ],
  bias_low_g: [
    {
      key: "bias",
      labelKey: `${STEP_FIELD_KEYS}.bias`,
      placeholderKey: `${STEP_FIELD_KEYS}.biasLowGPlaceholder`,
    },
  ],
  bias_gyro: [
    {
      key: "bias",
      labelKey: `${STEP_FIELD_KEYS}.bias`,
      placeholderKey: `${STEP_FIELD_KEYS}.biasGyroPlaceholder`,
    },
  ],
  centrifuge_test: [
    {
      key: "g",
      labelKey: `${STEP_FIELD_KEYS}.peakG`,
      type: "number",
      placeholder: "200",
    },
    {
      key: "result",
      labelKey: `${STEP_FIELD_KEYS}.result`,
      placeholderKey: `${STEP_FIELD_KEYS}.centrifugeResultPlaceholder`,
    },
  ],
  drop_test: [
    {
      key: "height_cm",
      labelKey: `${STEP_FIELD_KEYS}.heightCm`,
      type: "number",
      placeholder: "150",
    },
    {
      key: "result",
      labelKey: `${STEP_FIELD_KEYS}.result`,
      placeholderKey: `${STEP_FIELD_KEYS}.dropResultPlaceholder`,
    },
  ],
};

export const UNIT_TYPES = ["production", "engineering", "loaner"];

export const getUnitTypeLabel = (id) =>
  t(`admin.manufacturing.unitTypes.${id}`, { defaultValue: id });

export const getUnitTypeOptions = () =>
  UNIT_TYPES.map((id) => ({ id, label: getUnitTypeLabel(id) }));

// Kept for existing importers: `label` is a getter, so it is translated each
// time it is read (at render), not frozen at module load.
export const UNIT_TYPE_OPTIONS = UNIT_TYPES.map((id) => ({
  id,
  get label() {
    return getUnitTypeLabel(id);
  },
}));

/**
 * Turn a thrown v2Client error into a human-readable message.
 * Handles Pydantic validation arrays (400 { errors: [{loc, msg}] }),
 * { detail } shapes (404/409/400 transition errors), and plain messages.
 */
export const formatApiError = (err, fallback = t("common.errors.generic")) =>
  apiErrorMessage(err, fallback);

// Shared date/time formatters (match existing admin rows)
export const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString(dateLocale("en-US"), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(dateLocale("en-US"), {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/** Format a bare MAC as A1:B2:C3:D4:E5:F6 for display. */
export const formatMac = (mac = "") => {
  const bare = mac.replace(/[^0-9a-fA-F]/g, "").toUpperCase();
  return bare.match(/.{1,2}/g)?.join(":") || mac;
};

// ── Field label (the number printed on the case) ────────────────────
// An integer 0–999, not unique, nullable. `field_label_display` is the server's
// zero-padded string ("002"); prefer it and fall back to padding the raw int.
export const FIELD_LABEL_MAX = 999;

export const fieldLabelDisplay = (wearable) => {
  if (wearable?.field_label_display) return wearable.field_label_display;
  if (wearable?.field_label != null) {
    return String(wearable.field_label).padStart(3, "0");
  }
  return null;
};

// Display identity: the printed label is the primary ID (it's what's physically
// on the device the operator is holding); the serial is the precise, unique
// secondary ID. When no label is set, the serial is the primary and there is no
// secondary — i.e. current behaviour.
export const wearableIdentity = (wearable) => {
  const label = fieldLabelDisplay(wearable);
  if (label) {
    return {
      primary: label,
      secondary: wearable?.serial || null,
      hasLabel: true,
    };
  }
  return { primary: wearable?.serial || "—", secondary: null, hasLabel: false };
};

// Coerce a field-label input to { valid, value } where value is an int 0–999 or
// null (empty ⇒ null ⇒ clear). Non-integers / out-of-range ⇒ invalid.
export const parseFieldLabel = (raw) => {
  const s = String(raw ?? "").trim();
  if (s === "") return { valid: true, value: null };
  if (!/^\d+$/.test(s)) return { valid: false, value: null };
  const n = parseInt(s, 10);
  if (n < 0 || n > FIELD_LABEL_MAX) return { valid: false, value: null };
  return { valid: true, value: n };
};

/**
 * Build a wearable serial from its batch and sequence number, matching the
 * SIT-{hw}-{code}-{seq} convention, e.g. SIT-W1-2624-00001. The sequence is
 * zero-padded to 5 digits.
 */
export const buildSerial = (batch, sequence) => {
  if (!batch?.code) return "";
  const n = parseInt(sequence, 10);
  const seqStr =
    Number.isInteger(n) && n >= 0 ? String(n).padStart(5, "0") : "";
  const hw = batch.hw_revision || "W1";
  return `SIT-${hw}-${batch.code}-${seqStr}`;
};

/**
 * Increment the last run of digits in a string, preserving its width, e.g.
 * "SIT-W1-2624-00001" -> "SIT-W1-2624-00002". Returns the string unchanged if
 * it contains no digits.
 */
export const incrementTrailingNumber = (value = "") =>
  value.replace(/(\d+)(?!.*\d)/, (m) =>
    String(parseInt(m, 10) + 1).padStart(m.length, "0")
  );

/**
 * Batch code = 2-digit year + 2-digit ISO week, e.g. 2026 week 26 -> "2626".
 * Returns the code for the given date (defaults to today) so the form can
 * pre-fill it — users shouldn't have to work out the week number themselves.
 */
export const getIsoYearWeekCode = (date = new Date()) => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  // ISO weeks run Mon–Sun; shift to the Thursday of this week.
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  const yy = String(d.getUTCFullYear()).slice(-2);
  const ww = String(week).padStart(2, "0");
  return `${yy}${ww}`;
};
